<template>
	<div
		:aria-labelledby="`${groupId}-label`"
		class="amelipro-custom-selector"
		:role="unique ? 'radiogroup' : 'group'"
	>
		<div
			class="d-flex align-center amelipro-custom-selector__label-wrapper"
			:style="labelMarginBottomValue"
		>
			<p
				:id="groupId"
				:aria-describedby="labelDescription"
				class="mb-1 ap-grey--text text--darken-1 amelipro-custom-selector__label"
			>
				{{ groupLabel }}

				<span
					v-if="ariaRequired || multipleRequired"
					aria-hidden="true"
					class="d-inline-flex"
				>
					&nbsp;*
				</span>

				<span
					v-if="ariaRequired || multipleRequired"
					class="d-sr-only"
				>
					&nbsp;Champ obligatoire
				</span>
			</p>

			<slot name="labelInfo" />
		</div>

		<div class="d-flex flex-wrap justify-start amelipro-custom-selector__group">
			<div
				v-for="(item, index) in items"
				:key="index"
				class="d-block item-spacing amelipro-custom-selector__item"
				:class="{
					'item-per-line-2': itemsPerLine === 2,
					'item-per-line-3': itemsPerLine === 3,
				}"
			>
				<label
					class="d-block"
				>
					<input
						:id="`${groupId}-input-${index}`"
						v-model="selectedItems"
						:aria-describedby="checkErrorCondition ? `${groupId}-error` : undefined"
						:checked="item.isChecked"
						:name="`${groupId}-name`"
						:type="unique ? 'radio' : 'checkbox'"
						:value="item.value"
						@change="emitChangeEvent(item)"
					>

					<span class="select-item ap-grey--text text--darken-1 text-body-1 d-block">
						{{ item.label }}
					</span>
				</label>
			</div>
		</div>

		<AmeliproMessage
			v-if="checkErrorCondition"
			:id="`${groupId}-error`"
			no-icon
			text
			type="error"
		>
			<p class="mb-0">
				{{ error }}
			</p>
		</AmeliproMessage>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, ref } from 'vue';
	import { AmeliproCustomSelectorItem } from './types';
	import AmeliproMessage from '../AmeliproMessage/AmeliproMessage.vue';
	import { IndexedObject } from '../../types';

	const props = defineProps({
		ariaRequired: {
			type: Boolean,
			default: false,
		},
		groupId: {
			type: String,
			required: true,
		},
		groupLabel: {
			type: String,
			required: true,
		},
		items: {
			type: Array as PropType<AmeliproCustomSelectorItem[]>,
			default: () => [],
		},
		itemsPerLine: {
			type: Number,
			default: 1,
		},
		labelDescription: {
			type: String,
			default: undefined,
		},
		labelMarginBottom: {
			type: String,
			default: undefined,
		},
		multipleRequired: {
			type: Boolean,
			default: false,
		},
		multipleRequiredErrorMessage: {
			type: String,
			default: 'Vous devez cocher au moins deux options',
		},
		requiredErrorMessage: {
			type: String,
			default: 'Sélection obligatoire',
		},
		unique: {
			type: Boolean,
			default: false,
		},
	});

	const isAlreadyCheckedOnce = ref(false);
	const setValue = (() => {
		const checkedItemsTable: IndexedObject<string>[] = [];
		props.items.forEach((element) => {
			if (element.isChecked) {
				checkedItemsTable.push(element.value as unknown as IndexedObject<string>);
			}
		});

		if (checkedItemsTable.length > 0 && props.unique) {
			return checkedItemsTable[0].toString();
		} else if (props.unique) {
			return undefined;
		}
		return checkedItemsTable;
	});

	const selectedItems = ref<IndexedObject<string>[] | string | undefined>(setValue());

	const emit = defineEmits(['input']);
	const emitChangeEvent = (item: AmeliproCustomSelectorItem): void => {
		isAlreadyCheckedOnce.value = true;
		if (props.unique) {
			props.items.forEach((element) => {
				element.isChecked = false;
			});
			item.isChecked = true;
		} else {
			item.isChecked = !item.isChecked;
		}
		emit('input', selectedItems.value, props.groupId);
	};

	const isSingleRequiredErrorMessage = () => props.ariaRequired === true && (!selectedItems.value || selectedItems.value.length < 1);
	const isMultipleRequiredErrorMessage = () => props.multipleRequired === true && selectedItems.value && selectedItems.value.length < 2;
	const error = computed<string>(() => {
		if (isAlreadyCheckedOnce.value === true) {
			if (isSingleRequiredErrorMessage()) {
				return props.requiredErrorMessage;
			} else if (!props.unique && isMultipleRequiredErrorMessage()) {
				return props.multipleRequiredErrorMessage;
			}
		}
		return '';
	});
	const checkErrorCondition = computed<boolean>(() => {
		if (isAlreadyCheckedOnce.value === true) {
			if (isSingleRequiredErrorMessage()) {
				return true;
			} else if (!props.unique && isMultipleRequiredErrorMessage()) {
				return true;
			}
		}
		return false;
	});

	const labelMarginBottomValue = computed<IndexedObject>(() => ({ marginBottom: props.labelMarginBottom }));
</script>

<style lang="scss" scoped>

.amelipro-custom-selector__label {
	font-size: $font-size-sm;
	font-weight: $ap-font-weight-semi-bold;
}

.amelipro-custom-selector__item {
	width: 100%;

	&.item-per-line-2 {
		@media #{$media-up-sm} {
			width: calc(50% - 8px);
			margin-left: 8px;

			&:nth-child(2n+1) {
				margin-left: 0;
			}
		}
	}

	&.item-per-line-3 {
		@media #{$media-up-sm} {
			width: calc((100% / 3) - (16px / 3));
			margin-left: 8px;

			&:nth-child(3n+1) {
				margin-left: 0;
			}
		}
	}
}

.item-spacing {
	&:not(:last-child) {
		margin-bottom: 8px;
	}
}

input {
	position: absolute;
	width: 0;
	height: 0;
	opacity: 0;
}

.select-item {
	input+& {
		position: relative;
		padding: 0.75rem 3rem 0.75rem 1rem;
		border-radius: 5px;
		border: 1px solid $ap-grey;
		background-color: $ap-white;
		cursor: pointer;
	}

	input:checked+& {
		background-color: $ap-blue-lighten3;

		&::after {
			position: absolute;
			top: 1rem;
			right: 1rem;
			width: 1rem;
			height: 0.5rem;
			border-bottom: 2px solid $ap-blue-darken1;
			border-left: 2px solid $ap-blue-darken1;
			transform: rotate(-50deg);
			content: "";
		}
	}

	input:focus+& {
		border-color: $ap-blue-darken1;
		outline: 1px dotted $ap-grey-darken1;
	}

	input:disabled+& {
		background-color: $ap-grey-lighten2;
		cursor: default;

		&::after {
			border-bottom-color: $ap-grey-darken1;
			border-left-color: $ap-grey-darken1;
		}
	}
}

.check-icon {
	display: none;
	position: absolute;
	top: calc(50% - 16px);
	right: 0;

	input:checked+.select-item & {
		display: block;
	}
}
</style>
